import Cookies from "js-cookie";
import posthog from "posthog-js";
import { startCase, toLower } from "lodash";

import { ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED } from "~/shared/sharedConstants";

const COOKIE_VISIT_PRODUCT = "product";
const COOKIE_VISIT_ORDER_DETAILS_DATA = "orderDetailsData";
const COOKIE_USER_ID = "userID";
const COOKIE_VISIT_ID = "visitID";
const COOKIE_VISIT_CONSULT_ANSWERS = "consultAnswers";
const COOKIE_VISIT_API_TOKEN = "apiTokenJWT";
const COOKIE_VISIT_API_TOKEN_RESTRICTED = "apiTokenRestricted";
const COOKIE_VISIT_DIFFERENT_PHARMACY = "differentPharmacy";
const COOKIE_VISIT_EMAIL = "email";
const COOKIE_VISIT_PHONE = "phone";
const COOKIE_VISIT_GENDER = "gender";
const COOKIE_VISIT_USSTATE = "usState";
const COOKIE_VISIT_BIRTHDAY = "birthday";
const COOKIE_VISIT_FIRSTNAME = "firstName";
const COOKIE_VISIT_LASTNAME = "lastName";
const COOKIE_VISIT_LATEST_INVOICE_ID = "latestInvoiceID";
const COOKIE_VISIT_LATEST_INVOICE_PAYMENT_STATUS = "latestInvoicePaymentStatus";
const COOKIE_VISIT_FOLLOW_UP_FLAG = "visitTypeFollowUp";
const COOKIE_LATEST_ADDRESS = "latestAddress";
const COOKIE_STRIPE_LINK_PERSISTENT_TOKEN = "stripe.link.persistent_token";
const COOKIE_VISIT_SHIPMENT_METHOD = "shipmentMethod";
const COOKIE_REWARDFUL_AFFILIATE_ID = "rfaid";
const COOKIE_IS_PICKUP = "isPickup";
const COOKIE_BB_VARIANT = "betaBlockerVariant";
const COOKIE_ORDER_COMPLETE = "orderComplete";
const COOKIE_SMS_CONSENT = "smsConsent";
const COOKIE_ORDER_ITEMS = "orderItems";
const COOKIE_ORIGINAL_REFILL_ORDER_ITEMS = "originalRefillOrderItems";
const COOKIE_USER_ALREADY_EXISTS = "userAlreadyExists";
const COOKIE_UPGRADING_TO_2_PACK = "upgradingTo2Pack";
const SESSION_ACTIVE_SUBSCRIPTION = "SESSION_ACTIVE_SUBSCRIPTION";
const COOKIE_POST_CHECKOUT_INFO = "postCheckoutInfo";
const COOKIE_ACTIVE_SUBSCRIPTIONS = "activeSubscriptions";
const COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY =
  "isEditingPendingVisitPharmacy";

// expire in 2 weeks
const COOKIE_EXPIRES_DEFAULT = 14;

export function setVisitUserCookies(data) {
  setUserID(data.id);
  setVisitAPIToken(data.attributes.apiTokenJWT);
  setVisitAPITokenRestricted(data.attributes.restrictedToken);
  setVisitAPITokenLocalStorage(data.attributes.apiTokenJWT);
  setVisitEmail(toLower(data.attributes.email).trim());
  setVisitUSState(data.attributes.usState);
  setRewardfulAffiliateID(data.attributes.affiliateID);
  setSMSConsent(data.attributes.smsConsent);
  setShortIDMods(data);

  clearUserAlreadyExists();
}

function setShortIDMods(data) {
  for (let i = 2; i <= 7; i++) {
    setCookieWithExpiration(
      `shortIDMod${i}`,
      data.attributes[`shortIDMod${i}`],
      365
    );
  }
}

// Product
export function setVisitProduct(product) {
  clearOrderItems();
  setCookieWithExpiration(COOKIE_VISIT_PRODUCT, product);
}
export function getVisitProduct() {
  return Cookies.get(COOKIE_VISIT_PRODUCT);
}
export function clearVisitProduct() {
  clearOrderItems();
  Cookies.remove(COOKIE_VISIT_PRODUCT);
}
// Order Address
export function setOrderDetailsData(orderDetailsData) {
  setCookieWithExpiration(COOKIE_VISIT_ORDER_DETAILS_DATA, orderDetailsData);
}
export function getOrderDetailsData() {
  const cookie = Cookies.get(COOKIE_VISIT_ORDER_DETAILS_DATA);
  return cookie ? JSON.parse(decodeURIComponent(cookie)) : {};
}
// Visit ID
export function setVisitID(visitID) {
  return setCookieWithExpiration(COOKIE_VISIT_ID, visitID);
}
export function getVisitID() {
  return Cookies.get(COOKIE_VISIT_ID);
}
export function clearVisitID() {
  Cookies.remove(COOKIE_VISIT_ID);
}
// User ID
export function setUserID(userID) {
  return setCookieWithExpiration(COOKIE_USER_ID, userID);
}
export function getUserID() {
  return Cookies.get(COOKIE_USER_ID);
}
// Short ID Mods
export function getUserShortIDMod(num) {
  return Cookies.get(`shortIDMod${num}`);
}
// Latest Address
export function setLatestAddress(address) {
  return setCookieWithExpiration(
    COOKIE_LATEST_ADDRESS,
    JSON.stringify(address)
  );
}
export function getLatestAddress() {
  if (Cookies.get(COOKIE_LATEST_ADDRESS)) {
    return JSON.parse(Cookies.get(COOKIE_LATEST_ADDRESS));
  }

  return {};
}

// Consult Answers
export function setConsultAnswersJSON(consultAnswers) {
  if (consultAnswers) {
    return localStorage.setItem(
      COOKIE_VISIT_CONSULT_ANSWERS,
      JSON.stringify(consultAnswers).replace(
        /<([^>]+?)([^>]*?)>(.*?)<\/\1>/gi, // remove html tags and inner content
        ""
      )
    );
  }
}

export function getConsultAnswersJSON() {
  return JSON.parse(localStorage.getItem(COOKIE_VISIT_CONSULT_ANSWERS));
}
export function clearConsultAnswersJSON() {
  console.log("clearConsultAnswersJSON");
  localStorage.removeItem(COOKIE_VISIT_CONSULT_ANSWERS);
}

// API Token
export function setVisitAPIToken(visitAPIToken) {
  return setCookieWithExpiration(COOKIE_VISIT_API_TOKEN, visitAPIToken);
}
export function getVisitAPIToken() {
  // Weird caching bug? where using Cookies.get(COOKIE_VISIT_API_TOKEN ) incorrectly returns a cleared value
  // return Cookies.get(COOKIE_VISIT_API_TOKEN);
  return Cookies.get()[COOKIE_VISIT_API_TOKEN];
}
export function setVisitAPITokenLocalStorage(visitAPIToken) {
  return localStorage.setItem(COOKIE_VISIT_API_TOKEN, visitAPIToken);
}
export function getVisitAPITokenLocalStorage() {
  return localStorage.getItem(COOKIE_VISIT_API_TOKEN);
}
// API Token Restricted
export function setVisitAPITokenRestricted(visitAPITokenRestricted) {
  return setCookieWithExpiration(
    COOKIE_VISIT_API_TOKEN_RESTRICTED,
    visitAPITokenRestricted
  );
}
export function getVisitAPITokenRestricted() {
  // Weird caching bug? where using Cookies.get(COOKIE_VISIT_API_TOKEN ) incorrectly returns a cleared value
  // return Cookies.get(COOKIE_VISIT_API_TOKEN);
  return Cookies.get()[COOKIE_VISIT_API_TOKEN_RESTRICTED];
}
// Different Pharmacy
export function setVisitDifferentPharmacyJSON(differentPharmacy) {
  //   return setCookieWithExpiration(
  //     COOKIE_VISIT_DIFFERENT_PHARMACY,
  //     JSON.stringify(differentPharmacy)
  //   );
  return localStorage.setItem(
    COOKIE_VISIT_DIFFERENT_PHARMACY,
    JSON.stringify(differentPharmacy)
  );
}
export function getVisitDifferentPharmacyJSON() {
  //   const pharmacy = Cookies.get(COOKIE_VISIT_DIFFERENT_PHARMACY);
  //   return !isEmpty(pharmacy) ? JSON.parse(pharmacy) : "";
  return JSON.parse(localStorage.getItem(COOKIE_VISIT_DIFFERENT_PHARMACY));
}
export function getVisitDifferentPharmacy() {
  return localStorage.getItem(COOKIE_VISIT_DIFFERENT_PHARMACY);
}
// Email
export function setVisitEmail(email) {
  return setCookieWithExpiration(COOKIE_VISIT_EMAIL, email);
}
export function getVisitEmail() {
  return Cookies.get(COOKIE_VISIT_EMAIL) || "";
}
// Gender
export function setVisitGender(gender) {
  return setCookieWithExpiration(COOKIE_VISIT_GENDER, gender);
}
export function getVisitGender() {
  return Cookies.get(COOKIE_VISIT_GENDER);
}
// Birthday
export function setVisitBirthday(birthday) {
  return setCookieWithExpiration(COOKIE_VISIT_BIRTHDAY, birthday);
}
export function getVisitBirthday() {
  return Cookies.get(COOKIE_VISIT_BIRTHDAY);
}
// Name
export function setVisitFirstName(firstName) {
  return setCookieWithExpiration(COOKIE_VISIT_FIRSTNAME, firstName);
}
export function getVisitFirstName() {
  return Cookies.get(COOKIE_VISIT_FIRSTNAME) || "";
}
export function setVisitLastName(lastName) {
  return setCookieWithExpiration(COOKIE_VISIT_LASTNAME, lastName);
}
export function getVisitLastName() {
  return Cookies.get(COOKIE_VISIT_LASTNAME) || "";
}
export function getVisitFullName() {
  return startCase(
    toLower(`${getVisitFirstName()} ${getVisitLastName()}`).trim()
  );
}
// Visit US State
export function setVisitUSState(usState) {
  if (usState) {
    return setCookieWithExpiration(COOKIE_VISIT_USSTATE, usState);
  }
}
export function getVisitUSState() {
  return Cookies.get(COOKIE_VISIT_USSTATE);
}
// Visit Phone
export function setVisitPhone(phone) {
  if (phone) {
    return setCookieWithExpiration(COOKIE_VISIT_PHONE, phone);
  }
}
export function getVisitPhone() {
  return Cookies.get(COOKIE_VISIT_PHONE) || "";
}
// Shipment Method
export function setShipmentMethod(shipmentMethod) {
  return setCookieWithExpiration(COOKIE_VISIT_SHIPMENT_METHOD, shipmentMethod);
}
export function getShipmentMethod() {
  return Cookies.get(COOKIE_VISIT_SHIPMENT_METHOD);
}
export function clearShipmentMethod() {
  return Cookies.remove(COOKIE_VISIT_SHIPMENT_METHOD);
}
// Beta Blocker Variant
export function setBetaBlockerVariant(betaBlockerVariant) {
  return setCookieWithExpiration(COOKIE_BB_VARIANT, betaBlockerVariant);
}
export function getBetaBlockerVariant() {
  return Cookies.get(COOKIE_BB_VARIANT);
}
// Latest Visit Payment Status
export function setLatestVisitPaymentStatus(status) {
  return setCookieWithExpiration(
    COOKIE_VISIT_LATEST_INVOICE_PAYMENT_STATUS,
    status
  );
}
export function getLatestVisitPaymentStatus() {
  return Cookies.get(COOKIE_VISIT_LATEST_INVOICE_PAYMENT_STATUS);
}
// Latest Visit Invoice ID
export function setLatestVisitInvoiceID(invoiceID) {
  return setCookieWithExpiration(COOKIE_VISIT_LATEST_INVOICE_ID, invoiceID);
}
export function getLatestVisitInvoiceID() {
  return Cookies.get(COOKIE_VISIT_LATEST_INVOICE_ID) || "";
}
// Follow Up Visit Flag
export function setFollowUpVisitFlag(isFollowUpVisit) {
  return setCookieWithExpiration(COOKIE_VISIT_FOLLOW_UP_FLAG, isFollowUpVisit);
}
export function isFollowUpVisit() {
  return Cookies.get(COOKIE_VISIT_FOLLOW_UP_FLAG) == "yes";
}
export function setFollowUpVisitFlagToYes() {
  setFollowUpVisitFlag("yes");
}

// SMS Consent User (set from User record)
export function setSMSConsent(smsConsent) {
  return setCookieWithExpiration(COOKIE_SMS_CONSENT, smsConsent);
}
export function getSMSConsent() {
  return Cookies.get(COOKIE_SMS_CONSENT) === "true";
}

// Rewardful Affiliate ID
export function setRewardfulAffiliateID(affiliateID) {
  return setSessionCookie(COOKIE_REWARDFUL_AFFILIATE_ID, affiliateID);
}
export function getRewardfulAffiliateID() {
  return Cookies.get(COOKIE_REWARDFUL_AFFILIATE_ID);
}
// Stripe Link Persistent Token
export function setLinkPersistentTokenCookie(persistentToken) {
  Cookies.set(COOKIE_STRIPE_LINK_PERSISTENT_TOKEN, persistentToken, {
    expires: 365,
  });
}
export function getLinkPersistentTokenCookie() {
  return Cookies.get(COOKIE_STRIPE_LINK_PERSISTENT_TOKEN);
}
// Is Pickup (set in signup page modal)
export function setIsPickup(isPickup) {
  return setCookieWithExpiration(COOKIE_IS_PICKUP, isPickup || "");
}
export function getIsPickup() {
  return Cookies.get(COOKIE_IS_PICKUP);
}
// Order is complete
export function setOrderComplete() {
  return setCookieWithExpiration(COOKIE_ORDER_COMPLETE, true);
}
export function getOrderComplete() {
  return Cookies.get(COOKIE_ORDER_COMPLETE);
}
export function clearOrderComplete() {
  Cookies.remove(COOKIE_ORDER_COMPLETE);
}
export function getOrderItems() {
  const orderItems = Cookies.get(COOKIE_ORDER_ITEMS);
  const returnVal = orderItems ? JSON.parse(orderItems) : {};
  return returnVal;
}
export function getOrderItemsList() {
  const orderItemsByConsultationType = getOrderItems();
  const orderItemsList = [];
  for (let key in orderItemsByConsultationType) {
    orderItemsList.push({
      consultationType: key,
      ...orderItemsByConsultationType[key],
    });
    if (orderItemsByConsultationType[key].addons) {
      Object.keys(orderItemsByConsultationType[key].addons).forEach(
        (addonProductSlug) => {
          orderItemsList.push({
            consultationType: key,
            productSlug: addonProductSlug,
            isAddon: true,
            ...orderItemsByConsultationType[key].addons[addonProductSlug],
          });
        }
      );
    }
  }
  return orderItemsList;
}
export function getOrderItemAddonsList() {
  const orderItemsList = getOrderItemsList();
  return orderItemsList.filter(
    ({ productSlug }) => productSlug && productSlug !== getVisitProduct()
  );
}
export function setConsultationTypeOrderItem(consultationType, item) {
  /* Order items cookie looks like this: (1 product per consultation type for now)
    {
      pac: {
        productSlug: "prop-1-pack",
        quantity: 1,
        addons: {
          "prop-trial-pack-flavored": {
            subcategory: "tangerine",
          }
        }
      },
      sca: {
        productSlug: "sco-addon",
        quantity: 1,
      },
    }
  */
  const orderItems = getOrderItems();
  orderItems[consultationType] = {
    productSlug: item,
    quantity: 1,
    addons: {},
  };
  setCookieWithExpiration(COOKIE_ORDER_ITEMS, JSON.stringify(orderItems));
}
export function setConsultationTypeOrderItemAddon(
  consultationType,
  item,
  subcategory
) {
  /* Order items cookie looks like this: (1 product per consultation type for now)
    {
      pac: {
        productSlug: "prop-1-pack",
        quantity: 1,
      },
      sca: {
        productSlug: "sco-addon",
        quantity: 1,
      },
    }
  */
  const orderItems = getOrderItems();
  orderItems[consultationType] = {
    ...orderItems[consultationType],
    addons: {
      ...orderItems[consultationType].addons,
      [item]: {
        subcategory,
        quantity: 1,
      },
    },
  };
  setCookieWithExpiration(COOKIE_ORDER_ITEMS, JSON.stringify(orderItems));
}

export function removeConsultationTypeOrderItemAddon(consultationType, item) {
  const orderItems = getOrderItems();
  if (orderItems?.[consultationType]) {
    delete orderItems[consultationType].addons?.[item];
    setCookieWithExpiration(COOKIE_ORDER_ITEMS, JSON.stringify(orderItems));
  }
}

export function clearOrderItems() {
  Cookies.remove(COOKIE_ORDER_ITEMS);
}

export function clearUserCookies() {
  Cookies.remove(COOKIE_SMS_CONSENT);
  Cookies.remove(COOKIE_LATEST_ADDRESS);
  Cookies.remove(COOKIE_VISIT_PHONE);
  Cookies.remove(COOKIE_VISIT_GENDER);
  Cookies.remove(COOKIE_USER_ID);
}

export function clearVisitCookies() {
  Cookies.remove(COOKIE_BB_VARIANT);
  Cookies.remove(COOKIE_IS_PICKUP);
  Cookies.remove(COOKIE_ORDER_COMPLETE);
  Cookies.remove(COOKIE_ORDER_ITEMS);
  Cookies.remove(COOKIE_REWARDFUL_AFFILIATE_ID);
  Cookies.remove(COOKIE_UPGRADING_TO_2_PACK);
  Cookies.remove(COOKIE_VISIT_CONSULT_ANSWERS);
  Cookies.remove(COOKIE_VISIT_FOLLOW_UP_FLAG);
  Cookies.remove(COOKIE_VISIT_ID);
  Cookies.remove(COOKIE_VISIT_LATEST_INVOICE_ID);
  Cookies.remove(COOKIE_VISIT_LATEST_INVOICE_PAYMENT_STATUS);
  Cookies.remove(COOKIE_VISIT_ORDER_DETAILS_DATA);
  Cookies.remove(COOKIE_VISIT_PRODUCT);
  Cookies.remove(COOKIE_VISIT_SHIPMENT_METHOD);
  Cookies.remove(COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY);

  localStorage.removeItem(COOKIE_VISIT_DIFFERENT_PHARMACY);
  localStorage.removeItem(COOKIE_VISIT_CONSULT_ANSWERS);
  localStorage.removeItem(COOKIE_POST_CHECKOUT_INFO);
  localStorage.removeItem(COOKIE_ACTIVE_SUBSCRIPTIONS);
}

export function clearUserAndVisitCookies() {
  clearUserCookies();
  clearVisitCookies();
}

export function clearAuthCookies() {
  console.log("clearAuthCookies");
  Cookies.remove(COOKIE_VISIT_API_TOKEN);
  localStorage.removeItem(COOKIE_VISIT_API_TOKEN);
}

export function logVisitUserOut() {
  clearVisitCookies();
  clearUserCookies();
  clearAuthCookies();
  posthog.reset();
}

export function isLoggedIn() {
  // TODO: check expiration of JWT Token
  const token = getVisitAPIToken();
  return token != null && token != "";
}

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
export function storageAvailable(type) {
  var storage;
  try {
    storage = window[type];
    var x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === "QuotaExceededError" ||
        // Firefox
        e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
export function isLocalStorageAvailable() {
  return storageAvailable("localStorage");
}

export function isSessionStorageAvailable() {
  return storageAvailable("sessionStorage");
}

export function areCookiesAvailable() {
  try {
    const testKey = "storageTest";
    const testValue = "works!";

    Cookies.set(testKey, testValue);
    if (Cookies.get(testKey) != testValue) {
      return false;
    }
  } catch (error) {
    return false;
  }
  return true;
}

// persistent cookie with expiration
function setCookieWithExpiration(key, value, expires = null) {
  if (expires == null) {
    return Cookies.set(key, value, { expires: COOKIE_EXPIRES_DEFAULT });
  } else {
    return Cookies.set(key, value, { expires: expires });
  }
}

function setSessionCookie(key, value) {
  // omitting "expires" sets a session-only cookie
  return Cookies.set(key, value);
}

export function setUserAlreadyExists(email) {
  return setCookieWithExpiration(COOKIE_USER_ALREADY_EXISTS, email || "");
}

export function getUserAlreadyExists() {
  return Cookies.get(COOKIE_USER_ALREADY_EXISTS, "");
}

export function clearUserAlreadyExists() {
  return Cookies.remove(COOKIE_USER_ALREADY_EXISTS);
}

export function setUpgradingTo2Pack(value) {
  return setCookieWithExpiration(COOKIE_UPGRADING_TO_2_PACK, value);
}
export function getUpgradingTo2Pack() {
  return Cookies.get(COOKIE_UPGRADING_TO_2_PACK);
}
export function clearUpgradingTo2Pack() {
  return Cookies.remove(COOKIE_UPGRADING_TO_2_PACK);
}

export function setActiveSubscriptionBySubType(subType, sub) {
  localStorage.setItem(
    COOKIE_ACTIVE_SUBSCRIPTIONS,
    JSON.stringify({
      ...JSON.parse(localStorage.getItem(COOKIE_ACTIVE_SUBSCRIPTIONS)),
      [subType]: sub,
    })
  );
}
export function getActiveSubscriptionBySubType(subType) {
  const activeSub = JSON.parse(
    localStorage.getItem(COOKIE_ACTIVE_SUBSCRIPTIONS) || "{}"
  );
  return activeSub[subType];
}
export function clearActiveSubscriptionsBySubType() {
  localStorage.removeItem(COOKIE_ACTIVE_SUBSCRIPTIONS);
}

export function setPostCheckoutInfo(postCheckoutInfo) {
  return localStorage.setItem(
    COOKIE_POST_CHECKOUT_INFO,
    JSON.stringify(postCheckoutInfo)
  );
}
export function getPostCheckoutInfo() {
  return JSON.parse(localStorage.getItem(COOKIE_POST_CHECKOUT_INFO) || "{}");
}
export function clearPostCheckoutInfo() {
  localStorage.removeItem(COOKIE_POST_CHECKOUT_INFO);
}

export function setOrderItemSubcategory(consultationType, subcategory) {
  const orderItems = getOrderItems();
  orderItems[consultationType].subcategory = subcategory;
  setCookieWithExpiration(COOKIE_ORDER_ITEMS, JSON.stringify(orderItems));
}

export function getOrderItemSubcategory(consultationType) {
  const orderItems = getOrderItems();
  return orderItems[consultationType]?.subcategory;
}

export function clearOrderItemSubcategory(consultationType) {
  const orderItems = getOrderItems();
  delete orderItems[consultationType].subcategory;
  setCookieWithExpiration(COOKIE_ORDER_ITEMS, JSON.stringify(orderItems));
}

export function getOrderItemSubcategoryFromProductSlug(productSlug) {
  const orderItems = getOrderItems();
  for (const consultationType in orderItems) {
    if (orderItems[consultationType].productSlug === productSlug) {
      return orderItems[consultationType].subcategory;
    }
  }
  return null;
}

export function setOriginalRefillOrderItems(orderItems) {
  return localStorage.setItem(
    COOKIE_ORIGINAL_REFILL_ORDER_ITEMS,
    JSON.stringify(orderItems)
  );
}

export function getOriginalRefillOrderItems() {
  return JSON.parse(localStorage.getItem(COOKIE_ORIGINAL_REFILL_ORDER_ITEMS));
}

const mailOrderOnlyAddons = [ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED];
export function clearMailOrderOnlyAddonsForConsultationType(consultationType) {
  mailOrderOnlyAddons.forEach((addon) => {
    removeConsultationTypeOrderItemAddon(consultationType, addon);
  });
}

export function setIsEditingPendingVisitPharmacy(isEditing) {
  return setCookieWithExpiration(
    COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY,
    isEditing
  );
}

export function getIsEditingPendingVisitPharmacy() {
  return Cookies.get(COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY);
}

export function clearIsEditingPendingVisitPharmacy() {
  Cookies.remove(COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY);
}
